import * as THREE from 'three';
import ChunkController from "./ChunkController";
import { WorldGenerator } from './WorldGenerator';
import { chunkDistance, chunkSize } from './TerrainConstants';

export default class TerrainController {

    private readonly scene: THREE.Scene;
    private readonly chunks: Map<string, ChunkController>;
    private readonly generator: WorldGenerator;
    private elapsedTime = 0;

    constructor(scene: THREE.Scene, generator: WorldGenerator) {
        this.scene = scene;
        this.chunks = new Map<string, ChunkController>;
        this.generator = generator;
    }

    public update(
        cameraTarget: THREE.Vector3,
        cameraPosition: THREE.Vector3,
        deltaTime: number,
    ): void {

        this.elapsedTime += deltaTime;

        const posX = Math.round(cameraTarget.x / chunkSize) * chunkSize;
        const posZ = Math.round(cameraTarget.z / chunkSize) * chunkSize;

        const queue: { x: number, z: number, distance: number }[] = [];

        for (let x = posX - chunkDistance; x < posX + chunkDistance; x += chunkSize) {
            for (let z = posZ - chunkDistance; z < posZ + chunkDistance; z += chunkSize) {
                const distance = Math.sqrt(Math.pow(posX - x, 2) + Math.pow(posZ - z, 2));
                const key = this.getChunkKey(x, z);

                if (distance < chunkDistance) {
                    if (!this.chunks.has(key)) {
                        queue.push({ x, z, distance });
                    }
                }
            }
        }

        queue.toSorted((a, b) => (a.distance - b.distance)).slice(0, 10).forEach(item => {
            const { x, z } = item;
            const key = this.getChunkKey(x, z);

            const chunk = new ChunkController(
                this.scene,
                this.generator,
                new THREE.Vector3(x, 0, z)
            );

            this.chunks.set(key, chunk);
        });

        const chunksToRemove: ChunkController[] = [];

        this.chunks.forEach(x => {
            const distance = Math.sqrt(Math.pow(posX - x.getPosition().x, 2) + Math.pow(posZ - x.getPosition().z, 2));

            if (distance < chunkDistance) {
                x.update(cameraPosition, this.elapsedTime);
            }
            else {
                chunksToRemove.push(x);
            }
        });

        chunksToRemove.forEach(x => {
            this.chunks.delete(this.getChunkKey(x.getPosition().x, x.getPosition().z));
            x.destroy();
        });
    }

    private getChunkKey(x: number, z: number): string {
        return `${x}_${z}`;
    }
}
